// components/CustomSelect.jsx
import UserDropdownItem from "components/user_dropdown_item";
import React, { useEffect, useRef, useState } from "react";
import { UserOption } from "types";

interface CustomSelectProps {
  required?: boolean;
  options: UserOption[];
  onSelect?: (option: UserOption) => void;
}

const UserSelect: React.FC<CustomSelectProps> = ({
  options,
  onSelect,
  required,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  // Initialize selectedOption with the first option, or null if no options
  const [selectedOption, setSelectedOption] = useState<UserOption | null>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Effect to handle clicks outside the dropdown to close it
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  interface HandleOptionClick {
    (option: UserOption): void;
  }

  const handleOptionClick: HandleOptionClick = (option) => {
    setSelectedOption(option);
    setIsOpen(false);
    if (onSelect) {
      onSelect(option); // Call the parent's onSelect prop
    }
  };

  return (
    <div className="relative" ref={dropdownRef}>
      {" "}
      {/* Increased width slightly */}
      {/* Custom Select Trigger Button */}
      <button
        type="button"
        className="flex items-center justify-between w-full px-4 py-2 text-left bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 cursor-pointer"
        onClick={() => setIsOpen(!isOpen)}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
      >
        {selectedOption ? (
          <UserDropdownItem
            name={selectedOption.name}
            role={selectedOption.role}
          />
        ) : (
          <input
            type="text"
            required={required}
            placeholder="Select an option"
          />
        )}
        {/* Dropdown Arrow Icon */}
        <svg
          className={`h-5 w-5 text-gray-400 transform transition-transform ${
            isOpen ? "rotate-180" : ""
          }`}
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 20 20"
          fill="currentColor"
          aria-hidden="true"
        >
          <path
            fillRule="evenodd"
            d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
            clipRule="evenodd"
          />
        </svg>
      </button>
      {/* Dropdown Panel (List of Options) */}
      {isOpen && (
        <div className="absolute z-10 mt-1 w-full bg-white shadow-lg rounded-md border border-gray-200 py-1 max-h-60 overflow-auto">
          {options.map((option) => (
            <div
              key={option.id}
              className="hover:bg-gray-100 cursor-pointer"
              onClick={() => handleOptionClick(option)}
            >
              <UserDropdownItem name={option.name} role={option.role} />
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default UserSelect;

